import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { fmt, fmtB, fmtDate } from '@dcc-erp/shared';
import type { AgingBucket, VatSuspenseRow } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { HelpButton } from '../components/help/HelpButton';
import { PageHead } from '../components/layout';
import { RequestWriteOffModal } from '../components/vat-writeoff/RequestWriteOffModal';
import { VatWriteOffApprovalPanel } from '../components/vat-writeoff/VatWriteOffApprovalPanel';
import { useRealizeApBillVat } from '../lib/queries/ap';
import { useBranches } from '../lib/queries/branches';
import { useActiveCompanyId } from '../lib/queries/companies';
import {
  useCreateVatFiling,
  useFileVatBranches,
  useVatBranchBreakdown,
  useVatFilings,
  useVatRegister,
  useVatSummary,
} from '../lib/queries/vat';
import {
  useSuspenseWorklist,
  useVatWriteOffRequest,
  useVatWriteOffRequests,
} from '../lib/queries/vat-writeoff';

// Aging badge: prototype.css tokens (NO tailwind). OK→positive, APPROACHING→
// warning, OVERDUE→negative. null bucket (no deadline yet) renders neutral.
const AGING_BADGE: Record<AgingBucket, { cls: string; label: string }> = {
  OK: { cls: 'positive', label: 'ปกติ' },
  APPROACHING: { cls: 'warning', label: 'ใกล้ครบ' },
  OVERDUE: { cls: 'negative', label: 'เกินกำหนด' },
};

// Thai-month labels for the picker. JS Date is 0-indexed; index 0 = ม.ค.
const TH_MONTHS = [
  'ม.ค.',
  'ก.พ.',
  'มี.ค.',
  'เม.ย.',
  'พ.ค.',
  'มิ.ย.',
  'ก.ค.',
  'ส.ค.',
  'ก.ย.',
  'ต.ค.',
  'พ.ย.',
  'ธ.ค.',
] as const;

// Map period (YYYYMM) -> human label "เม.ย. 2026".
function periodLabel(period: string): string {
  const year = Number(period.slice(0, 4));
  const month = Number(period.slice(4, 6));
  return `${TH_MONTHS[month - 1]} ${year}`;
}

// VAT filing deadline = 15th of the following month (กรมสรรพากร rule).
function deadlineFor(period: string): Date {
  const year = Number(period.slice(0, 4));
  const month = Number(period.slice(4, 6));
  return new Date(Date.UTC(year, month, 15));
}

function toNum(v: string | number): number {
  return typeof v === 'number' ? v : Number(v);
}

export const Route = createFileRoute('/vat')({
  component: VatPage,
});

export function VatPage() {
  const companyId = useActiveCompanyId();

  // Default period = today's month (in YYYYMM). User can drill to any month
  // of the current calendar year via the monthly table below.
  const [period, setPeriod] = useState<string>(() => {
    const now = new Date();
    return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const currentYear = Number(period.slice(0, 4));

  const { data: register } = useVatRegister(companyId, period);
  const { data: summary } = useVatSummary(companyId, period);
  const { data: filings } = useVatFilings(companyId, currentYear);
  const { data: branchBreakdown } = useVatBranchBreakdown(companyId, period);
  const { data: branches } = useBranches(companyId, false);
  const createFiling = useCreateVatFiling(companyId);
  const fileBranches = useFileVatBranches(companyId);

  // Resolve branchId -> { taxBranch, nameTh } for the per-tax-branch table.
  const branchById = useMemo(() => new Map((branches ?? []).map((b) => [b.id, b])), [branches]);

  const sales = register?.output ?? [];
  const purchases = register?.input ?? [];
  const salesBase = toNum(summary?.salesBase ?? 0);
  const purchaseBase = toNum(summary?.purchaseBase ?? 0);
  const outputVat = toNum(summary?.outputVat ?? 0);
  const inputVat = toNum(summary?.inputVat ?? 0);
  const payable = toNum(summary?.payable ?? 0);

  // Build 12 monthly rows for the chosen calendar year, joining the
  // persisted filings (by period) so the table shows status + payable.
  const monthlyRows = useMemo(() => {
    const filingByPeriod = new Map((filings ?? []).map((f) => [f.period, f]));
    return Array.from({ length: 12 }, (_, idx) => {
      const monthPeriod = `${currentYear}${String(idx + 1).padStart(2, '0')}`;
      const filing = filingByPeriod.get(monthPeriod);
      return {
        period: monthPeriod,
        label: `${TH_MONTHS[idx]} ${currentYear}`,
        filing,
      };
    });
  }, [filings, currentYear]);

  const currentFiling = useMemo(
    () => (filings ?? []).find((f) => f.period === period),
    [filings, period],
  );

  const deadline = deadlineFor(period);
  const [nowMs] = useState(() => Date.now());
  const daysToDeadline = Math.max(
    0,
    Math.ceil((deadline.getTime() - nowMs) / (24 * 60 * 60 * 1000)),
  );

  // B3: deferred input-VAT (SUSPENSE) worklist. Data now comes from the
  // dedicated suspense-worklist route (VatSuspenseRow) so each row carries its
  // vatClaimDeadline / daysToDeadline / aging bucket; realize-vat is unchanged.
  const suspenseWorklist = useSuspenseWorklist(companyId);
  const suspenseRows = suspenseWorklist.data ?? [];
  // Header counter — how many rows are APPROACHING vs OVERDUE.
  const approachingCount = suspenseRows.filter((r) => r.bucket === 'APPROACHING').length;
  const overdueCount = suspenseRows.filter((r) => r.bucket === 'OVERDUE').length;
  const realizeVat = useRealizeApBillVat(companyId);
  const [realizeId, setRealizeId] = useState<string | null>(null);
  const [invNo, setInvNo] = useState('');
  const [invDate, setInvDate] = useState('');
  const [recvDate, setRecvDate] = useState('');

  // B5 (compose): write-off request modal (OVERDUE + no open request) and the
  // approval panel (row with an open request). Both keyed by the suspense row.
  const [writeOffRow, setWriteOffRow] = useState<VatSuspenseRow | null>(null);
  const [approvalRow, setApprovalRow] = useState<VatSuspenseRow | null>(null);

  const submitRealize = (billId: string): void => {
    if (!invNo || !invDate || !recvDate) return;
    realizeVat.mutate(
      {
        id: billId,
        data: { taxInvoiceNo: invNo, taxInvoiceDate: invDate, originalReceivedAt: recvDate },
      },
      {
        onSuccess: () => {
          setRealizeId(null);
          setInvNo('');
          setInvDate('');
          setRecvDate('');
        },
      },
    );
  };

  const submitFiling = (): void => {
    if (!companyId) return;
    const today = new Date();
    const filingDate = today.toISOString().slice(0, 10);
    // branchId omitted => head-office consolidated filing.
    createFiling.mutate({ period, filingDate });
  };

  // ยื่นแยกสาขา — one ภ.พ.30 per tax-branch with activity this period.
  const submitBranchFilings = (): void => {
    if (!companyId) return;
    fileBranches.mutate(period);
  };

  // Per-branch filings already persisted for this period (branchId !== null),
  // keyed by branchId so each breakdown row can show its filing status.
  const branchFilingByBranchId = useMemo(
    () =>
      new Map(
        (filings ?? [])
          .filter((f) => f.period === period && f.branchId)
          .map((f) => [f.branchId!, f]),
      ),
    [filings, period],
  );

  return (
    <div className="page">
      <PageHead
        crumbs={['รายงานและภาษี', 'ภาษี / VAT']}
        title={`ภาษีมูลค่าเพิ่ม — งวด ${periodLabel(period)}`}
        actions={
          <>
            <HelpButton guide="vat" />
            <select className="select" value={period} onChange={(e) => setPeriod(e.target.value)}>
              {monthlyRows.map((r) => (
                <option key={r.period} value={r.period}>
                  งวด: {r.label}
                </option>
              ))}
            </select>
            <button
              className="btn"
              onClick={() => {
                /* TODO(phase-3): wire CSV export of ภ.พ.30 */
              }}
            >
              <Icon name="download" size={14} /> ส่งออกแบบ ภ.พ.30
            </button>
            <button
              className="btn primary"
              onClick={submitFiling}
              disabled={!companyId || createFiling.isPending}
            >
              <Icon name="upload" size={14} /> ยื่นภ.พ.30
            </button>
          </>
        }
      />

      <div className="kpi-grid">
        <div className="kpi">
          <div className="label">
            <span className="dot" />
            ยอดขายรวม (ไม่รวม VAT)
          </div>
          <div className="value tnum">
            {fmtB(salesBase)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">{sales.length} ใบกำกับ</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--positive)' }} />
            ภาษีขาย (Output VAT)
          </div>
          <div className="value tnum">
            {fmt(outputVat)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">VAT 7%</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--info)' }} />
            ยอดซื้อรวม (ไม่รวม VAT)
          </div>
          <div className="value tnum">
            {fmtB(purchaseBase)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">{purchases.length} ใบกำกับ</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--warning)' }} />
            ภาษีต้องชำระ
          </div>
          <div className="value tnum" style={{ color: 'var(--brand)' }}>
            {fmt(payable)} <span className="unit">บาท</span>
          </div>
          <div className="trend down">กำหนดยื่น {fmtDate(deadline.toISOString().slice(0, 10))}</div>
        </div>
      </div>

      <div className="grid-2-eq">
        <div className="card">
          <div className="card-head">
            <div>
              <h3>รายงานภาษีขาย (Output VAT)</h3>
              <div className="sub">
                {sales.length} ใบกำกับภาษี · รวม VAT {fmt(outputVat)} บาท
              </div>
            </div>
            <button
              className="btn sm"
              onClick={() => {
                /* TODO(phase-3): wire CSV export */
              }}
            >
              <Icon name="download" size={12} /> CSV
            </button>
          </div>
          <div className="card-body flush">
            <div className="tbl-wrap" style={{ maxHeight: 380 }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>เลขที่ใบกำกับ</th>
                    <th>วันที่</th>
                    <th>ผู้ซื้อ</th>
                    <th>เลขผู้เสียภาษี</th>
                    <th className="num">มูลค่า</th>
                    <th className="num">VAT</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((r) => (
                    <tr key={r.id}>
                      <td className="code">{r.refKey ?? r.voucherNo}</td>
                      <td>{fmtDate(r.voucherDate)}</td>
                      <td>{r.party ?? '—'}</td>
                      <td className="code muted">{r.taxId ?? '—'}</td>
                      <td className="num tnum">{fmt(toNum(r.base))}</td>
                      <td className="num tnum">{fmt(toNum(r.vatAmount))}</td>
                    </tr>
                  ))}
                  <tr className="total">
                    <td colSpan={4}>รวม</td>
                    <td className="num tnum">{fmt(salesBase)}</td>
                    <td className="num tnum">{fmt(outputVat)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <div>
              <h3>รายงานภาษีซื้อ (Input VAT)</h3>
              <div className="sub">
                {purchases.length} ใบกำกับภาษี · รวม VAT {fmt(inputVat)} บาท
              </div>
            </div>
            <button
              className="btn sm"
              onClick={() => {
                /* TODO(phase-3): wire CSV export */
              }}
            >
              <Icon name="download" size={12} /> CSV
            </button>
          </div>
          <div className="card-body flush">
            <div className="tbl-wrap" style={{ maxHeight: 380 }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>เลขที่ใบกำกับ</th>
                    <th>วันที่</th>
                    <th>ผู้ขาย</th>
                    <th>เลขผู้เสียภาษี</th>
                    <th className="num">มูลค่า</th>
                    <th className="num">VAT</th>
                  </tr>
                </thead>
                <tbody>
                  {purchases.map((r) => (
                    <tr key={r.id}>
                      <td className="code">{r.refKey ?? r.voucherNo}</td>
                      <td>{fmtDate(r.voucherDate)}</td>
                      <td>{r.party ?? '—'}</td>
                      <td className="code muted">{r.taxId ?? '—'}</td>
                      <td className="num tnum">{fmt(toNum(r.base))}</td>
                      <td className="num tnum">{fmt(toNum(r.vatAmount))}</td>
                    </tr>
                  ))}
                  <tr className="total">
                    <td colSpan={4}>รวม</td>
                    <td className="num tnum">{fmt(purchaseBase)}</td>
                    <td className="num tnum">{fmt(inputVat)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>สรุปการคำนวณภาษี ภ.พ.30 — {periodLabel(period)}</h3>
          <span className={`badge ${currentFiling?.status === 'FILED' ? 'positive' : 'brand'}`}>
            {currentFiling?.status === 'FILED'
              ? 'ยื่นแล้ว'
              : currentFiling?.status === 'AMENDED'
                ? 'ยื่นเพิ่มเติม'
                : 'รอยื่น'}
          </span>
        </div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            <table className="tbl" style={{ border: '1px solid var(--border)' }}>
              <tbody>
                <tr>
                  <td>(1) ยอดขายที่ต้องเสียภาษี</td>
                  <td className="num tnum">{fmt(salesBase)}</td>
                </tr>
                <tr>
                  <td>(2) ภาษีขาย</td>
                  <td className="num tnum">{fmt(outputVat)}</td>
                </tr>
                <tr>
                  <td>(3) ยอดซื้อที่ขอภาษีคืน</td>
                  <td className="num tnum">{fmt(purchaseBase)}</td>
                </tr>
                <tr>
                  <td>(4) ภาษีซื้อ</td>
                  <td className="num tnum">{fmt(inputVat)}</td>
                </tr>
                <tr className="total">
                  <td>ภาษีต้องชำระ (2−4)</td>
                  <td className="num tnum" style={{ color: 'var(--brand)' }}>
                    {fmt(payable)}
                  </td>
                </tr>
              </tbody>
            </table>
            <div style={{ fontSize: 14, lineHeight: 1.7 }}>
              <div className="section-title">ขั้นตอนการยื่น</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span className="badge positive">
                    <span className="bdot" />
                    เสร็จ
                  </span>{' '}
                  ตรวจสอบใบกำกับภาษีทั้งหมด
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span className="badge positive">
                    <span className="bdot" />
                    เสร็จ
                  </span>{' '}
                  กระทบยอดกับสมุดรายวันขาย/ซื้อ
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span className={`badge ${currentFiling ? 'positive' : 'warning'}`}>
                    <span className="bdot" />
                    {currentFiling ? 'เสร็จ' : 'กำลังดำเนินการ'}
                  </span>{' '}
                  อนุมัติโดย ผจก. บัญชี
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span
                    className={`badge ${currentFiling?.status === 'FILED' ? 'positive' : 'neutral'}`}
                  >
                    {currentFiling?.status === 'FILED' ? 'เสร็จ' : 'ยังไม่เริ่ม'}
                  </span>{' '}
                  ยื่นแบบทางอินเทอร์เน็ต
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span className="badge neutral">ยังไม่เริ่ม</span> ชำระเงินผ่านธนาคาร
                </div>
              </div>
              <div className="divider" />
              <div className="muted" style={{ fontSize: 14 }}>
                วันที่ครบกำหนด:{' '}
                <b style={{ color: 'var(--brand)' }}>
                  {fmtDate(deadline.toISOString().slice(0, 10))}
                </b>{' '}
                ({daysToDeadline} วันที่เหลือ) · ระบบจะแจ้งเตือนอัตโนมัติทุก 24 ชม.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* B3: Deferred input-VAT (SUSPENSE) worklist — bills that booked to
          10190013 (ภาษีซื้อค้างรับ) because the original tax invoice had not
          been received at posting time. Once the invoice arrives, the accountant
          clicks "รับใบกำกับภาษีต้นฉบับแล้ว", fills the inline form, and the service
          posts the reversal JV (Dr 10190012 / Cr 10190013) and flips to CLAIMABLE. */}
      <div className="card">
        <div className="card-head">
          <div>
            <h3>ภาษีซื้อยังไม่ถึงกำหนด (รอใบกำกับภาษีต้นฉบับ)</h3>
            <div className="sub">
              {suspenseRows.length} รายการ · บันทึกที่บัญชี 10190013 (ภาษีซื้อค้างรับ) ·{' '}
              {approachingCount} ใกล้ครบ · {overdueCount} เกินกำหนด
            </div>
          </div>
        </div>
        <div className="card-body flush">
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>เลขที่ใบสำคัญ</th>
                  <th>วันที่บิล</th>
                  <th>ผู้ขาย</th>
                  <th className="num">ภาษีซื้อ (รอ)</th>
                  <th>ครบกำหนดขอคืน</th>
                  <th className="num">วันคงเหลือ</th>
                  <th>อายุ</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {suspenseRows.map((b) => (
                  <SuspenseRow
                    key={b.apBillId}
                    bill={b}
                    open={realizeId === b.apBillId}
                    onToggle={() => setRealizeId(realizeId === b.apBillId ? null : b.apBillId)}
                    invNo={invNo}
                    setInvNo={setInvNo}
                    invDate={invDate}
                    setInvDate={setInvDate}
                    recvDate={recvDate}
                    setRecvDate={setRecvDate}
                    onSubmit={() => submitRealize(b.apBillId)}
                    pending={realizeVat.isPending}
                    onWriteOff={() => setWriteOffRow(b)}
                    onApproval={() => setApprovalRow(b)}
                  />
                ))}
                {suspenseRows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="muted" style={{ textAlign: 'center' }}>
                      ไม่มีภาษีซื้อค้างรับในขณะนี้
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Per-tax-branch breakdown (Phase 42-D). Thai VAT is filed per
          registered tax-branch; this segments the company-wide ภ.พ.30 figure
          by the branch tag on the source AR invoices / AP bills. The total
          row equals the company-wide payable above. */}
      <div className="card">
        <div className="card-head">
          <div>
            <h3>แยกตามสาขา (tax-branch) — {periodLabel(period)}</h3>
            <div className="sub">
              ภาษีขาย / ภาษีซื้อ / ภาษีต้องชำระ แยกตามสาขาที่จดทะเบียนภาษีมูลค่าเพิ่ม
            </div>
          </div>
          <button
            className="btn primary sm"
            onClick={submitBranchFilings}
            disabled={
              !companyId ||
              fileBranches.isPending ||
              (branchBreakdown?.branches ?? []).every((b) => b.branchId === null)
            }
          >
            <Icon name="upload" size={12} /> ยื่นแยกสาขา
          </button>
        </div>
        <div className="card-body flush">
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>รหัสสาขา</th>
                  <th>สาขา</th>
                  <th className="num">ภาษีขาย</th>
                  <th className="num">ภาษีซื้อ</th>
                  <th className="num">ภาษีต้องชำระ</th>
                  <th>สถานะการยื่น</th>
                </tr>
              </thead>
              <tbody>
                {(branchBreakdown?.branches ?? []).map((row) => {
                  const branch = row.branchId ? branchById.get(row.branchId) : undefined;
                  const key = row.branchId ?? 'unassigned';
                  const filing = row.branchId
                    ? branchFilingByBranchId.get(row.branchId)
                    : undefined;
                  return (
                    <tr key={key}>
                      <td className="code">{branch?.taxBranch ?? '—'}</td>
                      <td>{branch?.nameTh ?? row.branchId ?? 'ไม่ระบุสาขา'}</td>
                      <td className="num tnum">{fmt(toNum(row.outputVat))}</td>
                      <td className="num tnum">{fmt(toNum(row.inputVat))}</td>
                      <td className="num tnum">{fmt(toNum(row.payable))}</td>
                      <td>
                        {row.branchId === null ? (
                          <span className="badge neutral">ไม่ระบุสาขา</span>
                        ) : filing ? (
                          <span
                            className={`badge ${filing.status === 'FILED' ? 'positive' : 'brand'}`}
                          >
                            {filing.status === 'FILED'
                              ? 'ยื่นแล้ว'
                              : filing.status === 'AMENDED'
                                ? 'ยื่นเพิ่มเติม'
                                : 'ร่าง'}
                          </span>
                        ) : (
                          <span className="badge neutral">ยังไม่ยื่น</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {(branchBreakdown?.branches?.length ?? 0) === 0 && (
                  <tr>
                    <td colSpan={6} className="muted" style={{ textAlign: 'center' }}>
                      ไม่มีข้อมูลภาษีในงวดนี้
                    </td>
                  </tr>
                )}
                <tr className="total">
                  <td colSpan={2}>รวมทุกสาขา</td>
                  <td className="num tnum">{fmt(toNum(branchBreakdown?.totalOutputVat ?? 0))}</td>
                  <td className="num tnum">{fmt(toNum(branchBreakdown?.totalInputVat ?? 0))}</td>
                  <td className="num tnum" style={{ color: 'var(--brand)' }}>
                    {fmt(toNum(branchBreakdown?.totalPayable ?? 0))}
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Monthly drill-down — 12 rows × current year. Clicking a row swaps
          the active period and reloads the register / summary above. */}
      <div className="card">
        <div className="card-head">
          <h3>การยื่นภ.พ.30 ปี {currentYear}</h3>
          <div className="sub">{(filings ?? []).length} งวดที่ยื่นแล้ว</div>
        </div>
        <div className="card-body flush">
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>งวด</th>
                  <th className="num">ภาษีขาย</th>
                  <th className="num">ภาษีซื้อ</th>
                  <th className="num">ภาษีต้องชำระ</th>
                  <th>สถานะ</th>
                  <th>วันที่ยื่น</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {monthlyRows.map((row) => {
                  const f = row.filing;
                  return (
                    <tr key={row.period} className={row.period === period ? 'active' : undefined}>
                      <td>{row.label}</td>
                      <td className="num tnum">{f ? fmt(toNum(f.outputVat)) : '—'}</td>
                      <td className="num tnum">{f ? fmt(toNum(f.inputVat)) : '—'}</td>
                      <td className="num tnum">{f ? fmt(toNum(f.payable)) : '—'}</td>
                      <td>
                        {f ? (
                          <span
                            className={`badge ${
                              f.status === 'FILED'
                                ? 'positive'
                                : f.status === 'AMENDED'
                                  ? 'warning'
                                  : 'neutral'
                            }`}
                          >
                            {f.status === 'FILED'
                              ? 'ยื่นแล้ว'
                              : f.status === 'AMENDED'
                                ? 'ยื่นเพิ่มเติม'
                                : 'ร่าง'}
                          </span>
                        ) : (
                          <span className="badge neutral">ยังไม่ยื่น</span>
                        )}
                      </td>
                      <td>{f ? fmtDate(f.filingDate) : '—'}</td>
                      <td>
                        <button
                          className="btn sm"
                          onClick={() => setPeriod(row.period)}
                          disabled={row.period === period}
                        >
                          ดูรายละเอียด
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* B5: deferred-VAT write-off — request modal (OVERDUE + no open request)
          and the engine approval panel (row with an open request). Both gated by
          a row selection; AP's inline realize action above is untouched. */}
      {writeOffRow && (
        <RequestWriteOffModal
          companyId={companyId}
          row={writeOffRow}
          onClose={() => setWriteOffRow(null)}
        />
      )}
      {approvalRow && companyId && (
        <WriteOffApprovalGate
          companyId={companyId}
          row={approvalRow}
          onClose={() => setApprovalRow(null)}
        />
      )}
    </div>
  );
}

// =============================================================================
// WriteOffApprovalGate — resolves the open VatWriteOffRequest for a suspense row
// (the worklist row only carries `hasOpenRequest`, not the request) then mounts
// VatWriteOffApprovalPanel. Lists the bill's requests, picks the open one
// (DRAFT / RETURNED / PENDING — anything not POSTED/VOID), and fetches its full
// detail (header + approvals chain) for the panel. Mounted only while a row's
// approval panel is open, so the route's default render touches no extra hooks.
// =============================================================================
const OPEN_STATUSES = new Set(['DRAFT', 'RETURNED', 'PENDING']);

interface WriteOffApprovalGateProps {
  companyId: string;
  row: VatSuspenseRow;
  onClose: () => void;
}

function WriteOffApprovalGate({ companyId, row, onClose }: WriteOffApprovalGateProps) {
  const { data: requests } = useVatWriteOffRequests(companyId, row.apBillId);
  // Prefer an engine-open request; fall back to the most recent header.
  const open = (requests ?? []).find((r) => OPEN_STATUSES.has(r.approvalStatus ?? r.status ?? ''));
  const target = open ?? (requests ?? [])[0];
  const { data: detail } = useVatWriteOffRequest(companyId, target?.id);

  if (!target) return null;
  // Use the full detail (with approvals) once loaded; the header is a placeholder.
  return (
    <VatWriteOffApprovalPanel companyId={companyId} request={detail ?? target} onClose={onClose} />
  );
}

// =============================================================================
// SuspenseRow — one row in the deferred-VAT worklist. Expands inline to capture
// the original tax invoice details before calling realize-vat (B3).
// =============================================================================
interface SuspenseRowProps {
  bill: VatSuspenseRow;
  open: boolean;
  onToggle: () => void;
  invNo: string;
  setInvNo: (v: string) => void;
  invDate: string;
  setInvDate: (v: string) => void;
  recvDate: string;
  setRecvDate: (v: string) => void;
  onSubmit: () => void;
  pending: boolean;
  onWriteOff: () => void;
  onApproval: () => void;
}

function SuspenseRow(p: SuspenseRowProps) {
  const aging = p.bill.bucket ? AGING_BADGE[p.bill.bucket] : null;
  // B5: an OVERDUE row with no open request can be written off; a row that
  // already carries an open request opens the engine approval panel instead.
  const canWriteOff = p.bill.bucket === 'OVERDUE' && !p.bill.hasOpenRequest;
  return (
    <>
      <tr>
        <td className="code">{p.bill.voucherNo}</td>
        <td>{fmtDate(p.bill.billDate)}</td>
        <td>{p.bill.vendorName}</td>
        <td className="num tnum">{fmt(toNum(p.bill.vatAmount))}</td>
        <td>{p.bill.vatClaimDeadline ? fmtDate(p.bill.vatClaimDeadline) : '—'}</td>
        <td className="num tnum">{p.bill.daysToDeadline ?? '—'}</td>
        <td>
          {aging ? (
            <span className={`badge ${aging.cls}`}>{aging.label}</span>
          ) : (
            <span className="badge neutral">—</span>
          )}
        </td>
        <td>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            <button className="btn sm" onClick={p.onToggle}>
              <Icon name="check" size={12} /> รับใบกำกับภาษีต้นฉบับแล้ว
            </button>
            {p.bill.hasOpenRequest ? (
              <button className="btn sm" onClick={p.onApproval}>
                <Icon name="eye" size={12} /> ดูสายอนุมัติ
              </button>
            ) : canWriteOff ? (
              <button className="btn sm danger" onClick={p.onWriteOff}>
                <Icon name="x" size={12} /> ตัดจำหน่าย
              </button>
            ) : null}
          </div>
        </td>
      </tr>
      {p.open && (
        <tr>
          <td colSpan={8} style={{ background: '#fafafa' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', padding: 8 }}>
              <label style={{ fontSize: 13 }}>
                เลขที่ใบกำกับภาษี
                <input
                  className="input"
                  value={p.invNo}
                  onChange={(e) => p.setInvNo(e.target.value)}
                />
              </label>
              <label style={{ fontSize: 13 }}>
                วันที่ใบกำกับ
                <input
                  className="input"
                  type="date"
                  value={p.invDate}
                  onChange={(e) => p.setInvDate(e.target.value)}
                />
              </label>
              <label style={{ fontSize: 13 }}>
                วันที่รับเอกสาร
                <input
                  className="input"
                  type="date"
                  value={p.recvDate}
                  onChange={(e) => p.setRecvDate(e.target.value)}
                />
              </label>
              <button className="btn primary sm" onClick={p.onSubmit} disabled={p.pending}>
                ยืนยันรับใบกำกับ
              </button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
